/**
 * CustomForm collection
 * Represents a single form
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/customFormElement';

class CustomForm extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object} methods                   - Collection RPC methods
	 * @property {method} methods.read              - Fetch custom form fields
	 *
	 * @property {method} methods.deleteCustomForm  - Delete custom form
	 * @property {method} methods.setCustomForm     - Update custom form fields
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return ['getCustomFormByName', this.customForm.formName];
			},

			/**
			 * @method
			 * @param {object} opts Request options
			 */
			deleteCustomForm(opts) {
				return ['deleteCustomForm', this.customForm.formName];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			setCustomForm(opts) {
				return ['setCustomForm', this.customForm];
			},
		};
	}

	/**
	 * @property {object}   parsers       - Collection parsers
	 * @property {function} parsers.read  - Parses result of fetched form
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {object} data Form data
			 * @param {object} opts Request options
			 */
			read(data, opts) {
				if (!data) return null;

				// taking the raw form data and parsing it so customFormElementView can deal
				/* eslint-disable */
				const elements = JSON.parse(data.formData.replace(/\\\"/g, '"'));
				try {
					const userVals = this.customForm.userValues;
					_.each(elements, (element, idx, all) => {
						if (this.duplicateKeys.indexOf(element.label.key) !== -1) {
							delete all[idx];
							return;
						}
						const name = element.label.key
							? element.label.key
							: this.model.makeName(element.label.en);

						if (element.type === 'checkboxes') {
							// if we have user values, loop though and create userValues array
							if (_.isEmpty(userVals)) return;

							_.each(element.value, (elem, idx, all) => {
								const userVal = userVals[`${name}-${elem.key}`];
								if (!userVal) return;

								if (!element.userValues) {
									element.userValues = [];
								}

								element.userValues.push(userVal);
							});
						} else if (userVals[name]) {
							element.userValue = userVals[name];
						}
					});

					return elements;
				} catch (e) {
					console.warn('could not parse fetched elements, err: %o', e);
				}

				return null;
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;
		super(models, opts);

		/**
		 * @type {object}
		 */
		const customForm = (this.customForm = {
			formName: '',
			formData: '',
			userValues: {},
			duplicateKeys: [],
			readOnly: false,
		});

		if (opts.formName) {
			customForm.formName = opts.formName;
			customForm.userValues = opts.userValues || {};
			this.readOnly = opts.readOnly || false;
			this.duplicateKeys = opts.duplicateKeys || [];
		}
	}

	/**
	 * @method
	 * @param {object} formData Form data
	 * @return {object} Form data
	 */
	getDisplayByData(formData) {
		if (this.length <= 1) return formData;

		// take the form data and return it with actual labels
		_.each(formData, (detail, idx, all) => {
			const idxAr = idx.split('cfe-');
			if (idxAr.length <= 1) return;

			const element = this.find({name: idxAr[1]});
			if (element) {
				// this data is in the form, use the label
				const label = element.getDisplayLabel();
				all[label] = element.getDisplayValue(detail);
			} else {
				// not in the form, just trim the 'cfe-', likely the form has changed
				all[idxAr[1]] = detail;
			}
			delete all[idx];
		});

		return formData;
	}

	/**
	 * Cleans form data, removes cfe-
	 * @method
	 * @param {object} formData Form data
	 * @return {object} Form data
	 */
	submitArgs(formData) {
		_.each(formData, (dataValue, dataKey, all) => {
			if (dataKey.slice(0, 4) !== 'cfe-') return;

			all[dataKey.slice(4)] = dataValue.toString().replace(/"/g, '');
			delete all[dataKey];
		});
		return formData;
	}

	/** @type {string} */
	static get __name__() {
		return 'CustomForm';
	}
}

export default CustomForm;

Netx.CustomForm = CustomForm;
